export function getPublicCatalogPath(tenantSlug: string, productSlug?: string) {
  const safeTenantSlug = tenantSlug.trim()
  const basePath = `/${safeTenantSlug}/catalog`
  if (!productSlug) return basePath
  return `${basePath}/${productSlug.trim()}`
}

export function getPublicCatalogUrl(tenantSlug: string, productSlug?: string, origin?: string) {
  const baseOrigin = origin ?? (typeof window !== 'undefined' ? window.location.origin : '')
  return `${baseOrigin}${getPublicCatalogPath(tenantSlug, productSlug)}`
}

export function buildWhatsAppShareLink(message: string, phone?: string | null) {
  const encodedMessage = encodeURIComponent(message)
  const cleanPhone = phone ? phone.trim().replace(/\D+/g, '') : ''
  if (cleanPhone) {
    return `https://wa.me/${cleanPhone}?text=${encodedMessage}`
  }
  return `https://wa.me/?text=${encodedMessage}`
}

export function calculateDiscountedPrice(
  priceCents: number,
  promotion?: { discountPct?: number | null; discountAmt?: number | null } | null
): {
  finalPriceCents: number
  originalPriceCents: number
  hasDiscount: boolean
  discountLabel: string | null
} {
  if (!promotion) {
    return {
      finalPriceCents: priceCents,
      originalPriceCents: priceCents,
      hasDiscount: false,
      discountLabel: null,
    }
  }

  let finalPriceCents = priceCents
  let discountLabel: string | null = null

  if (typeof promotion.discountPct === 'number' && promotion.discountPct > 0) {
    const discount = Math.round(priceCents * (promotion.discountPct / 100))
    finalPriceCents = Math.max(0, priceCents - discount)
    discountLabel = `-${promotion.discountPct}%`
  } else if (typeof promotion.discountAmt === 'number' && promotion.discountAmt > 0) {
    finalPriceCents = Math.max(0, priceCents - promotion.discountAmt)
    discountLabel = 'Oferta'
  }

  return {
    finalPriceCents,
    originalPriceCents: priceCents,
    hasDiscount: finalPriceCents < priceCents,
    discountLabel,
  }
}

export function buildCatalogShareMessage(params: {
  tenantName: string
  productName?: string
  priceLabel?: string
  url: string
}) {
  const parts = [`Hola, te comparto ${params.tenantName}.`]

  if (params.productName) {
    const pricePart = params.priceLabel ? ` ${params.priceLabel}` : ''
    parts.push(`Mira *${params.productName}*${pricePart}.`)
  } else {
    parts.push('Mira el catálogo completo.')
  }

  parts.push(`Link: ${params.url}`)
  return parts.join(' ')
}

export function buildWhatsAppDirectOrderMessage(params: {
  tenantName: string
  items: Array<{ name: string; quantity: number; priceCents: number; currency?: string; sku?: string }>
  url: string
}): string {
  const lines: string[] = [
    `Hola! Vi el catálogo de *${params.tenantName}* y me gustaría pedir:`,
  ]

  let totalCents = 0
  const currency = params.items[0]?.currency || 'USD'

  for (const item of params.items) {
    const itemTotal = item.priceCents * item.quantity
    totalCents += itemTotal
    const skuPart = item.sku ? ` (SKU: ${item.sku})` : ''
    lines.push(`• ${item.quantity}x *${item.name}*${skuPart} - ${formatMoney(item.priceCents, currency)} c/u`)
  }

  lines.push('')
  lines.push(`*Total estimado:* ${formatMoney(totalCents, currency)}`)
  lines.push(`Catálogo: ${params.url}`)

  return lines.join('\n')
}

export function buildWhatsAppAppointmentMessage(params: {
  tenantName: string
  serviceName: string
  priceLabel?: string
  url: string
}): string {
  const priceText = params.priceLabel ? ` (${params.priceLabel})` : ''
  return [
    `Hola! Vi *${params.serviceName}*${priceText} en su catálogo de *${params.tenantName}*.`,
    'Me gustaría consultar disponibilidad de día y horario.',
    `Link: ${params.url}`,
  ].join('\n')
}

export function buildWhatsAppProductInquiryMessage(params: {
  tenantName: string
  productName: string
  priceLabel?: string
  url: string
}): string {
  const priceText = params.priceLabel ? ` (${params.priceLabel})` : ''
  return [
    `Hola! Vi *${params.productName}*${priceText} en el catálogo de *${params.tenantName}*.`,
    '¿Tienen disponibilidad y cómo es el proceso de entrega?',
    `Link: ${params.url}`,
  ].join('\n')
}

export function formatMoney(amountCents: number, currency: string = 'USD') {
  const amount = amountCents / 100
  try {
    return new Intl.NumberFormat('es-VE', {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(amount)
  } catch {
    return `${currency} ${amount.toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }
}
